import React from 'react';
import { Check } from 'lucide-react';

const HomePage = ({ setCurrentPage }) => {
  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-br from-green-100 via-blue-50 to-green-50 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
            Track Your Betting Journey
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-700">
            Professional bet tracking for tipsters and punters
          </p>
          <div className="space-x-4">
            <button
              onClick={() => setCurrentPage('tracker')}
              className="bg-gradient-to-r from-green-400 to-blue-400 text-white px-8 py-3 rounded-full font-semibold hover:opacity-90 transition transform hover:scale-105"
            >
              Get Started
            </button>
            <button
              onClick={() => setCurrentPage('pricing')}
              className="border-2 border-green-400 text-green-700 px-8 py-3 rounded-full font-semibold hover:bg-green-50 transition"
            >
              View Plans
            </button>
          </div>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Sports We Cover</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { name: 'Rugby League', icon: '🏉' },
              { name: 'Soccer', icon: '⚽' },
              { name: 'UFC/MMA', icon: '🥊' },
              { name: 'NFL', icon: '🏈' },
              { name: 'NBA', icon: '🏀' },
              { name: 'AFL', icon: '🏏' },
              { name: 'MLB', icon: '⚾' },
              { name: 'Tennis', icon: '🎾' },
              { name: 'Boxing', icon: '🥊' },
              { name: 'Rugby Union', icon: '🏉' }
            ].map((sport, index) => (
              <div key={index} className="text-center p-6 rounded-lg bg-gradient-to-br from-green-50 to-blue-50 hover:shadow-lg transition">
                <div className="text-4xl mb-2">{sport.icon}</div>
                <p className="font-medium">{sport.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Platform Features</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-green-600">For Tipsters</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check className="text-green-500 mr-2 mt-1" size={16} />
                  <span>Track tips in units</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 mr-2 mt-1" size={16} />
                  <span>Multi-leg bet support</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 mr-2 mt-1" size={16} />
                  <span>Monitor ROI and win rates</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 mr-2 mt-1" size={16} />
                  <span>Build your reputation</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 mr-2 mt-1" size={16} />
                  <span>Client management tools</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-blue-600">For Punters</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check className="text-blue-500 mr-2 mt-1" size={16} />
                  <span>Copy tipster bets</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-blue-500 mr-2 mt-1" size={16} />
                  <span>Convert units to your bankroll</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-blue-500 mr-2 mt-1" size={16} />
                  <span>Mark bets placed on time</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-blue-500 mr-2 mt-1" size={16} />
                  <span>Modify odds and stakes</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-blue-500 mr-2 mt-1" size={16} />
                  <span>Compare tipster performance</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-purple-600">Analytics Dashboard</h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check className="text-purple-500 mr-2 mt-1" size={16} />
                  <span>Unit-based performance tracking</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-purple-500 mr-2 mt-1" size={16} />
                  <span>Historical bet analysis</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-purple-500 mr-2 mt-1" size={16} />
                  <span>Profit/loss reports</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-purple-500 mr-2 mt-1" size={16} />
                  <span>Sport-specific breakdowns</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-gradient-to-r from-green-400 to-blue-400 text-white py-16">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl font-bold mb-4">Join Our Community</h2>
          <p className="text-xl mb-8">Connect with fellow punters and tipsters</p>
          <div className="flex justify-center gap-6">
            <a
              href="#"
              className="bg-white text-gray-800 px-6 py-3 rounded-full font-medium hover:bg-gray-100 transition"
            >
              Discord: @PureDiscord
            </a>
            <a
              href="#"
              className="bg-white text-gray-800 px-6 py-3 rounded-full font-medium hover:bg-gray-100 transition"
            >
              Instagram: @PureDegen
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
